import { useInfiniteQuery } from '@tanstack/react-query';
import _camelCase from 'lodash/camelCase';
import _snakeCase from 'lodash/snakeCase';
import { Tecton_Graphql } from '..';
import { FeatureFlags, useUserSettings } from '../../components/context/UserSettingsContext';
import { transformJobs } from '../../components/jobs/jobsTransform';
import { graphql } from '../gql';
import { FcoTypeFilter, GetJobsRequest, TaskType } from '../gql/graphql';
import { fetchJobs } from '../jobs';
import { convertKeysToCamelCase, convertKeysToSnakeCase } from '../utils';

export const jobsQuery = graphql(`
  query GetJobs($getJobsRequest: GetJobsRequest) {
    GetJobs(GetJobsRequest: $getJobsRequest) {
      jobs {
        fullId {
          mostSignificantBits
          leastSignificantBits
        }
        id
        workspace
        featureView
        featureServiceName
        featureStartTime
        featureEndTime
        fvType {
          label
          color
          backgroundColor
          value
          checked
        }
        fvTaskTypeForDisplay {
          label
          color
          backgroundColor
          value
          checked
        }
        latestStatus {
          label
          color
          backgroundColor
          value
          checked
        }
        lastTaskStateChange
        duration
        attempts {
          dataSourceType
          computeIdentity
          windowStartTime
          windowEndTime
          featureStartTime
          featureEndTime
          materializationState
          stateMessage
          terminationReason
          spotInstanceFailure
          materializationTaskCreatedAt
          attemptNumber
          sparkClusterEnvironmentVersion
          runPageUrl
          attemptCreatedAt
          duration
          allowForcedRetry
          allowOverwriteRetry
          allowCancel
          allowRestart
          writeToOnlineFeatureStore
          writeToOfflineFeatureStore
          tectonManagedAttemptId
          attemptConsumption {
            onlineRowsWritten
            offlineRowsWritten
            jobDuration
          }
          consumptionInfo {
            timeBucketStart
            unitsConsumed
            metric
            sourceId
            featureViewName
            workspace
            featureViewId {
              mostSignificantBits
              leastSignificantBits
            }
            details {
              offlineStoreType
            }
          }
          materializationTaskId {
            mostSignificantBits
            leastSignificantBits
          }
          materializationAttemptId
        }
        numAttempts
        manuallyTriggered
        planId {
          mostSignificantBits
          leastSignificantBits
        }
        allowRetry
        allowCancel
        allowOverwrite
        writesOnline
        writesOffline
        scheduleErrorMessage
      }
      pagination {
        page
        perPage
        nextPageToken
        sortKey
        sortDirection
        total
      }
    }
  }
`);

// Switch to this once we do the full switch over
export const useGetInfiniteJobsGQL = (args: GetJobsRequest, keepPreviousData: boolean) => {
  const { user } = useUserSettings();
  const principalId = user?.sub; // Get current user's oktaId for permission calculation

  return useInfiniteQuery(
    ['infinite-jobs-gql', args, principalId], // Include principalId in query key
    async ({ pageParam = '', signal }) => {
      const variables = {
        getJobsRequest: {
          ...args,
          principalId, // Add principalId for permission calculation
          pagination: {
            ...args.pagination,
            pageToken: pageParam,
          },
        },
      };
      // eslint-disable-next-line
      const response = await Tecton_Graphql.request<any>({
        document: jobsQuery,
        variables: convertKeysToCamelCase(variables),
        signal,
      });

      return convertKeysToSnakeCase(response.GetJobs);
    },
    {
      refetchOnWindowFocus: false,
      keepPreviousData,
      getNextPageParam: (lastPage) => {
        return lastPage?.pagination?.nextPageToken || '';
      },
      select: (data) => {
        return {
          pageParams: data.pageParams,
          pages: data.pages.map((page) => page),
        };
      },
      notifyOnChangeProps: ['data', 'error', 'isFetching', 'isLoading', 'isFetchingNextPage'],
    }
  );
};

export const useGetInfiniteJobs = (args: GetJobsRequest, keepPreviousData: boolean) => {
  const { featureEnabled, user } = useUserSettings();
  const useGraphQL = featureEnabled(FeatureFlags.JOBS_GQL);
  const principalId = user?.sub; // Get current user's oktaId for permission calculation

  // Create wrapped fetch function that passes principalId to GraphQL
  const fetchFunction = useGraphQL
    ? (args: GetJobsRequest, pageParam: string, signal: AbortSignal) =>
        fetchJobsGQL(args, pageParam, signal, principalId)
    : doFetchJobs;

  if (args.featureServices && args.featureServices.length > 0) {
    // The API requires including an fcoTypeFilter when filtering by feature services
    args.fcoType = FcoTypeFilter.FeatureServiceOnly;
    args.taskType = [TaskType.DatasetGeneration];
  }

  return useInfiniteQuery(
    ['infinite-jobs', args, principalId], // Include principalId in query key
    ({ pageParam = '', signal }) => fetchFunction(args, pageParam, signal as AbortSignal),
    {
      refetchOnWindowFocus: false,
      keepPreviousData,
      getNextPageParam: (lastPage) => {
        return lastPage?.pagination?.[useGraphQL ? 'nextPageToken' : 'next_page_token'] || '';
      },
      select: (data) => {
        return {
          pageParams: data.pageParams,
          pages: data.pages.map((page) => (useGraphQL ? page : transformJobs(page))),
        };
      },
      notifyOnChangeProps: ['data', 'error', 'isFetching', 'isLoading', 'isFetchingNextPage'],
    }
  );
};

const doFetchJobs = (args: GetJobsRequest, pageParam: string, signal: AbortSignal) => {
  const params = {
    ...args,
    pagination: {
      ...args.pagination,
      page_token: pageParam,
    },
  };

  return fetchJobs(params as GetJobsRequest, signal);
};

const fetchJobsGQL = async (args: GetJobsRequest, pageParam: string, signal: AbortSignal, principalId?: string) => {
  if (args?.pagination?.sortKey) args.pagination.sortKey = _snakeCase(args.pagination.sortKey);

  const variables = {
    getJobsRequest: {
      ...args,
      principalId, // Add principalId for permission calculation
      pagination: {
        ...args.pagination,
        pageToken: pageParam,
      },
    },
  };
  // eslint-disable-next-line
  const response = await Tecton_Graphql.request<any>({
    document: jobsQuery,
    variables: convertKeysToCamelCase(variables),
    signal,
  });

  if (response?.GetJobs?.pagination?.sortKey)
    response.GetJobs.pagination.sortKey = _camelCase(response?.GetJobs?.pagination?.sortKey);

  return response.GetJobs;
};
